import { useCallback, useMemo } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { EyeIcon, EyeOffIcon } from 'lucide-react';

import {
  type ImageGalleryItem,
  NewImageGallery,
} from '@modules/_shared/components/image-gallery/NewImageGallery';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import {
  GET_CMA_AGENCY_TEAM_MEMBERS,
  UPDATE_CMA_REPORT_AGENCY_TEAM_MEMBERS,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import {
  type SavedTeamMember,
  mergeUnseenTeamMembers,
  resolveAgencyTeamMembers,
} from '@modules/reports/cma-reports/step-agent/agencyTeamMembers';
import { useLocale } from '@shared/hooks/locale';

type StepAgencyTeamMembersProps = {
  reportId: string;
};

const StepAgencyTeamMembers = ({ reportId }: StepAgencyTeamMembersProps) => {
  const { t } = useLocale();

  const {
    data,
    loading,
    error: loadError,
    refetch,
  } = useQuery(GET_CMA_AGENCY_TEAM_MEMBERS, {
    variables: { id: reportId },
    fetchPolicy: 'cache-and-network',
  });

  const [updateTeamMembers, { error, reset }] = useMutation(
    UPDATE_CMA_REPORT_AGENCY_TEAM_MEMBERS,
  );

  const report = data?.cma_reports_by_pk;

  const { members, unseen } = useMemo(
    () =>
      resolveAgencyTeamMembers(
        report?.broker?.default_team?.teams_users ?? [],
        report?.agency_team_members,
      ),
    [report],
  );

  const galleryItems = useMemo(
    (): ImageGalleryItem[] =>
      members.map((member, index) => {
        const image = member.user.user_images?.[0]?.image;
        return {
          id: member.user_id,
          title: [member.user.first_name, member.user.last_name]
            .filter(Boolean)
            .join(' '),
          description: member.title || t('No title'),
          order_nr: index,
          image: { id: image?.id ?? member.user_id, url: image?.url },
          is_visible: member.is_visible,
        };
      }),
    [members, t],
  );

  const allVisible = members.every(member => member.is_visible);

  const save = useCallback(
    (nextMembers: SavedTeamMember[], unseenVisible?: boolean) => {
      const agencyTeamMembers = mergeUnseenTeamMembers(
        nextMembers,
        unseen,
        unseenVisible,
      );
      updateTeamMembers({
        variables: { id: reportId, agency_team_members: agencyTeamMembers },
        optimisticResponse: {
          update_cma_reports_by_pk: {
            __typename: 'cma_reports',
            id: reportId,
            agency_team_members: agencyTeamMembers,
            updated_at: new Date().toISOString(),
          },
        },
      });
    },
    [reportId, unseen, updateTeamMembers],
  );

  const handleToggle = useCallback(
    (userId: string) =>
      save(
        members.map(member =>
          member.user_id === userId
            ? { ...member, is_visible: !member.is_visible }
            : member,
        ),
      ),
    [members, save],
  );

  const handleToggleAll = () =>
    save(
      members.map(member => ({ ...member, is_visible: !allVisible })),
      !allVisible,
    );

  const handleReorder = useCallback(
    (userId: string, targetPosition: number) => {
      const nextMembers = members.filter(member => member.user_id !== userId);
      const moved = members.find(member => member.user_id === userId);
      if (moved == null) {
        return;
      }
      nextMembers.splice(targetPosition - 1, 0, moved);
      save(nextMembers);
    },
    [members, save],
  );

  const renderVisibilityToggle = useCallback(
    (item: ImageGalleryItem) => (
      <Tooltip
        title={item.is_visible ? t('Hide from report') : t('Show in report')}
      >
        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            handleToggle(item.id);
          }}
        >
          {item.is_visible ? <EyeIcon size={18} /> : <EyeOffIcon size={18} />}
        </IconButton>
      </Tooltip>
    ),
    [t, handleToggle],
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        {loadError != null && report == null ? (
          <Alert
            severity="error"
            sx={{ m: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                {t('Retry')}
              </Button>
            }
          >
            {t('Failed to load data')}
          </Alert>
        ) : !loading && members.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', p: 2 }}>
            {t('No team members to show')}
          </Typography>
        ) : (
          <NewImageGallery
            images={galleryItems}
            onReorderImage={handleReorder}
            renderRowAction={renderVisibilityToggle}
            toolbarActions={
              <Tooltip title={allVisible ? t('Hide all') : t('Show all')}>
                <IconButton onClick={handleToggleAll}>
                  {allVisible ? (
                    <EyeOffIcon size={20} />
                  ) : (
                    <EyeIcon size={20} />
                  )}
                </IconButton>
              </Tooltip>
            }
            viewMode="list"
            enableViewMode={false}
            disableRowSelection
            showTitleAndDescription
            squareThumbnails
          />
        )}
      </Box>
      <MutationErrorSnackbar error={error ?? null} onClose={reset} />
    </>
  );
};

export default StepAgencyTeamMembers;
